// Copyright (c) 2026 Aravind Adari. All rights reserved.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  CollectionReference,
  DocumentReference,
} from "firebase/firestore";
import { auth, db } from "../firebase";

export interface ClientFeatures {
  payroll: boolean;
  leaves: boolean;
  reports: boolean;
  advances: boolean;
}

export interface ClientContextType {
  /** Firestore client ID from claims. Empty for platform admin. */
  clientId: string;
  /** Human-readable company name loaded from clients/{clientId} */
  clientName: string;
  /** Client-level role: receptionist | manager | admin | "" */
  role: string;
  /** True when user has platformRole === "super_admin" in claims */
  isPlatformAdmin: boolean;
  /** True while auth state + claims are loading */
  isLoading: boolean;
  /** Number of days in the past a user can edit timesheet entries (default 7) */
  timesheetEditDays: number;
  /** Max employees allowed (0 = unlimited) */
  employeeLimit: number;
  /** Max branches allowed (0 = unlimited) */
  branchLimit: number;
  /** Feature flags for this client */
  features: ClientFeatures;
  /**
   * Returns a Firestore CollectionReference scoped to
   * clients/{clientId}/{name}. Safe to call in hooks (stable reference).
   */
  col: (name: string) => CollectionReference;
  /**
   * Returns a Firestore DocumentReference scoped to
   * clients/{clientId}/{name}/{docId}.
   */
  docRef: (name: string, docId: string) => DocumentReference;
}

const defaultFeatures: ClientFeatures = { payroll: true, leaves: true, reports: true, advances: true };

const ClientContext = createContext<ClientContextType>({
  clientId: "",
  clientName: "",
  role: "",
  isPlatformAdmin: false,
  isLoading: true,
  timesheetEditDays: 7,
  employeeLimit: 0,
  branchLimit: 0,
  features: defaultFeatures,
  col: (name) => collection(db, "clients", "__none__", name),
  docRef: (name, docId) => doc(db, "clients", "__none__", name, docId),
});

export function ClientProvider({ children }: { children: ReactNode }) {
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [role, setRole] = useState("");
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [timesheetEditDays, setTimesheetEditDays] = useState(7);
  const [employeeLimit, setEmployeeLimit] = useState(0);
  const [branchLimit, setBranchLimit] = useState(0);
  const [features, setFeatures] = useState<ClientFeatures>(defaultFeatures);

  useEffect(() => {
    let configUnsub: (() => void) | null = null;
    const cleanupConfig = () => { if (configUnsub) { configUnsub(); configUnsub = null; } };

    const authUnsub = onAuthStateChanged(auth, async (user) => {
      cleanupConfig();
      if (!user) {
        setClientId("");
        setClientName("");
        setRole("");
        setIsPlatformAdmin(false);
        setIsLoading(false);
        return;
      }
      try {
        const tokenResult = await getIdTokenResult(user, true);
        const claims = tokenResult.claims as Record<string, unknown>;

        if (claims.platformRole === "super_admin") {
          setIsPlatformAdmin(true);
          setRole("super_admin");
          setClientId("");
          setClientName("Platform Admin");
          setIsLoading(false);
        } else {
          const cid = (claims.clientId as string) || "";
          setClientId(cid);
          setRole((claims.role as string) || "");
          setIsPlatformAdmin(false);

          // Live-subscribe to client config so timesheetEditDays and other
          // settings update in real-time without requiring a page refresh.
          if (cid) {
            configUnsub = onSnapshot(doc(db, "clients", cid), (snap) => {
              if (snap.exists()) {
                const data = snap.data();
                setClientName((data.name as string) || cid);
                setTimesheetEditDays(data.timesheetEditDays != null ? (Number(data.timesheetEditDays) || 7) : 7);
                setEmployeeLimit(data.employeeLimit != null ? (Number(data.employeeLimit) || 0) : 0);
                setBranchLimit(data.branchLimit != null ? (Number(data.branchLimit) || 0) : 0);
                if (data.features && typeof data.features === "object") {
                  setFeatures({
                    payroll: data.features.payroll !== false,
                    leaves: data.features.leaves !== false,
                    reports: data.features.reports !== false,
                    advances: data.features.advances !== false,
                  });
                }
              } else {
                setClientName(cid);
              }
              setIsLoading(false);
            }, (err) => {
              console.error("[ClientContext] Failed to subscribe to clients/" + cid + ":", err);
              setClientName(cid);
              setIsLoading(false);
            });
          } else {
            setIsLoading(false);
          }
        }
      } catch {
        setRole("");
        setIsPlatformAdmin(false);
        setIsLoading(false);
      }
    });
    return () => { authUnsub(); cleanupConfig(); };
  }, []);

  const col = useCallback(
    (name: string): CollectionReference =>
      collection(db, "clients", clientId || "__none__", name),
    [clientId]
  );

  const docRef = useCallback(
    (name: string, docId: string): DocumentReference =>
      doc(db, "clients", clientId || "__none__", name, docId),
    [clientId]
  );

  return (
    <ClientContext.Provider
      value={{ clientId, clientName, role, isPlatformAdmin, isLoading, timesheetEditDays, employeeLimit, branchLimit, features, col, docRef }}
    >
      {children}
    </ClientContext.Provider>
  );
}

/** Hook to access the current client context in any component or page. */
export const useClient = () => useContext(ClientContext);
